import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { FBRInvoice, InvoiceData } from '@/components/invoice/FBRInvoice'
import {
  getOrders,
  getCustomers,
  getProducts,
  updateOrderStatus,
  getCompanySettings,
  CompanySettings,
  DEFAULT_COMPANY_SETTINGS,
} from '@/services/firestore'
import { FileText, Eye, Search, Filter, Trash2, Undo2 } from 'lucide-react'
import { submitFBRReversal, buildFBRPayload } from '@/services/fbr'
import { updateDoc, doc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { formatDisplayDate } from '@/lib/utils'

export const Route = createFileRoute('/admin/reports')({
  component: InvoicesPage,
})

function InvoicesPage() {
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [customersMap, setCustomersMap] = useState<Record<string, any>>({})
  const [productsMap, setProductsMap] = useState<Record<string, any>>({})
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceData | null>(null)
  const [companySettings, setCompanySettings] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS)

  useEffect(() => {
    loadInvoices()
  }, [])

  const loadInvoices = async () => {
    setLoading(true)
    try {
      // Fetch all completed orders, customers, products and company settings in parallel
      const [orders, customers, products, company] = await Promise.all([
        getOrders({ status: 'completed' }),
        getCustomers(),
        getProducts(),
        getCompanySettings(),
      ])
      setInvoices(orders)
      setCompanySettings(company)
      
      // Build lookup maps for customer and product data
      const custMap: Record<string, any> = {}
      customers.forEach((c: any) => { custMap[c.id] = c })
      setCustomersMap(custMap)
      
      const prodMap: Record<string, any> = {}
      products.forEach((p: any) => { prodMap[p.id] = p })
      setProductsMap(prodMap)
    } catch (err) {
      console.error('Error fetching invoices:', err)
    } finally {
      setLoading(false)
    }
  }

  const filteredInvoices = invoices.filter(inv => {
    const s = search.toLowerCase()
    const invNo = (inv.invoiceNumber || inv.orderNumber || '').toLowerCase()
    const matchesSearch =
      invNo.includes(s) ||
      (inv.customerName?.toLowerCase() || '').includes(s)

    if (!matchesSearch) return false

    if (filterType === 'fbr') return inv.invoiceType === 'fbr'
    if (filterType === 'standard') return inv.invoiceType !== 'fbr'
    return true
  })

  const f = (n: number) => n?.toLocaleString('en-PK', { minimumFractionDigits: 2 }) || '0.00'

  const handlePreview = (order: any) => {
    // Look up customer data from the customers collection for old orders missing metadata
    const customer = order.customerId ? customersMap[order.customerId] : null
    
    // Reconstruct InvoiceData from the saved order
    const mappedItems = (order.items || []).map((it: any, i: number) => {
      const qty = it.quantity || 1;
      const rate = it.unitPrice ?? it.price ?? 0;
      const itemTotal = it.total ?? (qty * rate);
      const taxRate = it.taxRate ?? 18;
      const taxAmt = it.taxAmount ?? Math.round(itemTotal * taxRate / 100);
      
      // For old orders missing UOM, look it up from the product record
      const product = it.productId ? productsMap[it.productId] : null
      const uom = it.uom || product?.unit || 'PCS'

      return {
        serial: i + 1,
        code: it.code || `ITM${i + 1}`,
        product: it.name,
        hsCode: it.hsCode || product?.hsCode || '8448.5900',
        saleType: 'Goods at standard rate (default)',
        uom,
        quantity: qty,
        unitPrice: rate,
        discount: 0,
        saleExTax: itemTotal,
        tax: taxAmt,
        subtotal: itemTotal + taxAmt,
      };
    });

    const resolvedDate = order.invoiceDate || order.date || (order.createdAt?.toDate ? order.createdAt.toDate().toISOString() : (order.createdAt || new Date().toISOString()));

    const payload: InvoiceData = {
      invoiceNumber: order.invoiceNumber || order.orderNumber || '-',
      date: resolvedDate,
      poNumber: order.poNumber,
      deliveryNumber: order.deliveryNumber,
      docNumber: order.docNumber,
      sellerName: companySettings.companyName,
      sellerAddress: companySettings.address,
      sellerCity: companySettings.city,
      sellerProvince: companySettings.province,
      sellerNtn: companySettings.ntn,
      sellerGst: companySettings.stn,
      sellerPhone: companySettings.phone || '',
      sellerEmail: companySettings.email || '',
      // Use order-level customer fields first, then fall back to looked-up customer record
      buyerName: order.customerName || customer?.name || 'Walk-in Customer',
      buyerAddress: order.customerAddress || customer?.address || '',
      buyerCity: order.customerCity || customer?.city || '',
      buyerNtn: order.customerNtn || customer?.ntn || '',
      buyerCnic: order.customerCnic || customer?.cnic || '',
      buyerStn: order.customerStn || customer?.stn || '',
      buyerPhone: order.customerPhone || customer?.phone || '',
      items: mappedItems,
      subtotal: order.subtotal || mappedItems.reduce((s: number, it: any) => s + it.saleExTax, 0),
      taxRate: order.taxRate ?? 18,
      taxAmount: order.taxAmount ?? mappedItems.reduce((s: number, it: any) => s + it.tax, 0),
      discountAmount: order.discountAmount || 0,
      totalAmount: order.totalAmount || 0,
    }

    // Extract FBR invoice number from ALL possible storage locations
    const extractedFbrNumber =
      order.fbrInvoiceNumber ||
      order.fbrPayload?.fbrInvoiceNumber ||
      order.fbrPayload?.InvoiceNumber ||
      order.fbrPayload?.Invoice_Id ||
      order.fbrPayload?.fbrResponse?.invoiceNumber ||
      order.fbrPayload?.fbrResponse?.Invoice_Id ||
      order.FBR_Invoice_Number ||
      order.Invoice_Id ||
      null;

    if (extractedFbrNumber) {
      payload.fbrInvoiceNumber = extractedFbrNumber;
    }
    if (order.fbrPayload?.usid || order.usid) {
      payload.usid = order.fbrPayload?.usid || order.usid;
    }
    if (order.fbrStatus) {
      payload.fbrStatus = order.fbrStatus;
    }
    payload.posId = companySettings.posId || 'POS-001';

    setSelectedInvoice(payload)
  }

  const handleReverseFBR = async (inv: any) => {
    // 1. Check 3-day limit
    const rawDate = inv.invoiceDate || inv.date || (inv.createdAt?.toDate ? inv.createdAt.toDate() : inv.createdAt);
    const invDate = rawDate instanceof Date ? rawDate : new Date(rawDate);
    const diffTime = new Date().getTime() - invDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    
    if (diffDays > 3) {
      alert("FBR limit exceeded: Invoices can only be legally reversed or cancelled within 3 days of generation.");
      return;
    }

    if (!confirm(`Are you sure you want to REVERSE this FBR invoice?\n\nFBR No: ${inv.fbrInvoiceNumber || inv.invoiceNumber}\nAmount: PKR ${f(inv.totalAmount)}\n\nThis will send a 'Return' payload to FBR to nullify the tax liability, and restore local stock. This action CANNOT be undone.`)) {
      return;
    }

    try {
      // 2. Build the Reversal Payload using the existing order data
      const mappedItems = (inv.items || []).map((it: any, i: number) => ({
        code: it.code || `ITM${i+1}`,
        name: it.name || it.product,
        quantity: it.quantity || 1,
        hsCode: it.hsCode || "8448.5900",
        uom: it.uom || "PCS",
        taxRate: it.taxRate ?? 18,
        unitPrice: it.unitPrice ?? it.price ?? 0,
        totalAmount: it.total ?? ((it.quantity || 1) * (it.unitPrice ?? it.price ?? 0)),
        taxAmount: it.taxAmount ?? 0,
        discount: 0
      }));

      const reversalPayloadProps = {
         invoiceType: "Sale Return" as const,
         invoiceNumber: inv.invoiceNumber || inv.orderNumber || '-',
         invoiceDate: inv.invoiceDate || inv.date || (inv.createdAt?.toDate ? inv.createdAt.toDate() : inv.createdAt),
         posId: companySettings.posId || '1',
         buyerNtn: inv.customerNtn || inv.buyerNtn,
         buyerCnic: inv.customerCnic || inv.buyerCnic,
         buyerName: inv.customerName || inv.buyerName || 'Walk-in Customer',
         items: mappedItems,
         totalAmount: inv.totalAmount || 0,
         totalQuantity: inv.items?.length || 1,
         totalSaleValue: inv.subtotal || 0,
         totalTax: inv.taxAmount || 0,
         discount: inv.discountAmount || 0,
         paymentMethod: inv.paymentMethod || 'cash',
         sellerName: companySettings.companyName,
         sellerNTN: companySettings.ntn,
         sellerProvince: companySettings.province || 'Punjab',
         sellerAddress: `${companySettings.address}${companySettings.city ? `, ${companySettings.city}` : ''}`,
      };

      const payload = buildFBRPayload(reversalPayloadProps);

      // We explicitly override the invoice reference number to match the EXACT one submitted to FBR originally, just in case
      if (inv.fbrInvoiceNumber && typeof inv.fbrInvoiceNumber === 'string') {
          payload.invoiceRefNo = inv.fbrInvoiceNumber; 
      }

      // 3. Submit to FBR API
      const r = await submitFBRReversal(payload);
      
      const validationStatus = r.validationResponse?.status?.toLowerCase?.() || '';
      const validationStatusCode = r.validationResponse?.statusCode || '';
      const isRejected = r.isLocalError || validationStatus === 'error' || validationStatus === 'failed' || validationStatusCode === '400';

      if (isRejected) {
        alert(`❌ FBR Reversal Failed\n\nError: ${r.localErrorMessage || r.validationResponse?.error || 'Unknown Error'}\nCode: ${validationStatusCode}`);
        return;
      }

      // 4. Update Database: Cancel order (restores stock) and update FBR status
      await updateOrderStatus(inv.id, 'cancelled', 'Reversed via FBR 3-day policy return');
      await updateDoc(doc(db, 'orders', inv.id), { fbrStatus: 'reversed' });

      alert('✅ Invoice successfully reversed with FBR and stock restored!');
      
      // Update local UI
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'cancelled', fbrStatus: 'reversed' } : i));

    } catch (err: any) {
      alert('Failed to process reversal: ' + (err.message || err));
    }
  }

  return (
    <div className="p-4 pt-14 lg:pt-5 lg:p-5 bg-[#f0f5ff] min-h-screen">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            Invoice History
          </h1>
          <p className="text-sm text-slate-500 mt-1">View all previously generated Local and FBR invoices.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded-xl border border-slate-200/60 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by invoice number or customer name..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 bg-slate-50"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400 hidden sm:block" />
          <select
            value={filterType} onChange={e => setFilterType(e.target.value)}
            className="pl-3 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none bg-slate-50 w-full sm:w-auto"
          >
            <option value="all">All Invoices</option>
            <option value="standard">Standard / Local</option>
            <option value="fbr">FBR Invoices</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 uppercase tracking-wider text-[11px] font-semibold">
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Invoice No</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    <div className="animate-pulse flex flex-col items-center">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2" />
                      Loading invoices...
                    </div>
                  </td>
                </tr>
              ) : filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500 font-medium">
                    No invoices found.
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                      {formatDisplayDate(inv.invoiceDate || inv.date || inv.createdAt)}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {inv.invoiceNumber || inv.orderNumber || '-'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-medium truncate max-w-[200px]" title={inv.customerName}>
                      {inv.customerName || 'Walk-in Customer'}
                    </td>
                    <td className="px-4 py-3">
                      {inv.invoiceType === 'fbr' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 uppercase tracking-wider border border-blue-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />FBR
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wider">
                          Local
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-slate-800">
                      PKR {f(inv.totalAmount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {inv.invoiceType === 'fbr' ? (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${inv.fbrStatus === 'accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                          {inv.fbrStatus || 'Pending'}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handlePreview(inv)}
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg"
                        >
                          <Eye className="w-3.5 h-3.5" />Preview
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(`Are you sure you want to delete this invoice?\n\nCustomer: ${inv.customerName || 'Walk-in'}\nAmount: PKR ${f(inv.totalAmount)}\n\nThis action cannot be undone.`)) return;
                            try {
                              await updateOrderStatus(inv.id, 'cancelled', 'Deleted from invoice history');
                              setInvoices(prev => prev.filter(i => i.id !== inv.id));
                            } catch (err: any) {
                              alert('Failed to delete: ' + (err.message || err));
                            }
                          }}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-500 bg-red-50 hover:bg-red-100 px-2.5 py-1.5 rounded-lg"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        
                        {/* FBR Reverse Button */}
                        {inv.invoiceType === 'fbr' && inv.fbrStatus === 'accepted' && (
                          <button
                            onClick={() => handleReverseFBR(inv)}
                            title="Reverse/Cancel with FBR (Within 3 days)"
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 bg-amber-50 hover:bg-amber-100 px-2.5 py-1.5 rounded-lg"
                          >
                            <Undo2 className="w-3.5 h-3.5" /> FBR Undo
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Modal */}
      {selectedInvoice && (
        <FBRInvoice
          data={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  )
}